(() => {
  if (customElements.get('hd-hero-slider')) {
    return;
  }

  class HdHeroSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.pointerStart = null;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
      if (this.abortController) {
        return;
      }

      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };
      this.slides = Array.from(this.querySelectorAll('[data-hd-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hd-hero-dot]'));
      this.previousButton = this.querySelector('[data-hd-hero-previous]');
      this.nextButton = this.querySelector('[data-hd-hero-next]');
      this.interval = Number(this.dataset.interval) || 6000;
      this.autoplay = this.dataset.autoplay === 'true';

      if (this.slides.length <= 1) {
        this.hideControls();
        return;
      }

      this.previousButton?.addEventListener('click', () => this.showPrevious(), listenerOptions);
      this.nextButton?.addEventListener('click', () => this.showNext(), listenerOptions);
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.showSlide(index), listenerOptions);
      });
      this.addEventListener('mouseenter', () => this.stop(), listenerOptions);
      this.addEventListener('mouseleave', () => this.start(), listenerOptions);
      this.addEventListener('focusin', () => this.stop(), listenerOptions);
      this.addEventListener('focusout', () => this.start(), listenerOptions);
      this.addEventListener('keydown', (event) => this.handleKeydown(event), listenerOptions);
      this.addEventListener('pointerdown', (event) => this.handlePointerDown(event), listenerOptions);
      this.addEventListener('pointerup', (event) => this.handlePointerUp(event), listenerOptions);
      this.addEventListener('pointercancel', () => { this.pointerStart = null; }, listenerOptions);

      this.update();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.abortController?.abort();
      this.abortController = null;
      this.pointerStart = null;
    }

    start() {
      if (!this.autoplay || this.prefersReducedMotion || this.slides.length <= 1) {
        return;
      }

      this.stop();
      this.timer = window.setInterval(() => this.showNext(), this.interval);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    showPrevious() {
      this.showSlide((this.currentIndex - 1 + this.slides.length) % this.slides.length);
    }

    showNext() {
      this.showSlide((this.currentIndex + 1) % this.slides.length);
    }

    showSlide(index) {
      this.currentIndex = index;
      this.update();
      this.start();
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showNext();
      }
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse') {
        return;
      }
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    handlePointerUp(event) {
      if (!this.pointerStart || event.pointerType === 'mouse') {
        return;
      }

      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }
      deltaX > 0 ? this.showPrevious() : this.showNext();
    }

    update() {
      this.slides.forEach((slide, index) => {
        const isActive = index === this.currentIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          if (isActive) {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });
      });

      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
      });
    }

    hideControls() {
      this.querySelector('[data-hd-hero-controls]')?.setAttribute('hidden', '');
    }
  }

  customElements.define('hd-hero-slider', HdHeroSlider);
})();
